import type { RunProgress } from "../api/types";

/** Overall completion in 0..1: whole containers done, plus the current one's share of one. */
function fraction(progress: RunProgress): number {
  if (progress.containers_total === 0) {
    return 0;
  }
  const within = progress.files_total > 0 ? progress.files_done / progress.files_total : 0;
  return Math.min(1, (progress.containers_done + within) / progress.containers_total);
}

/**
 * Files per minute of *actual work*, or null when there is not yet enough to say.
 *
 * Skipped-as-unchanged files are excluded from the rate: they settle thousands per minute at the
 * start of a resumed run, and counting them would make the ETA collapse to "under a minute" while
 * hours of real processing remain. The remaining count still shrinks as skips settle, so pairing
 * it with the work-only rate converges on an honest estimate from above.
 *
 * Measured from the run's own start rather than between polls, so a slow container does not make
 * the estimate lurch. Returns null rather than a guess when the rate would be meaningless — an
 * ETA invented from two data points is worse than no ETA.
 */
function ratePerMinute(progress: RunProgress, startedAt: string | null): number | null {
  const worked = progress.files_done - progress.files_unchanged;
  if (!startedAt || !progress.updated_at || worked < 1) {
    return null;
  }
  const minutes = (new Date(progress.updated_at).getTime() - new Date(startedAt).getTime()) / 60000;
  return minutes > 0.5 ? worked / minutes : null;
}

function formatEta(remaining: number, rate: number | null): string {
  if (rate === null || rate <= 0) {
    return "";
  }
  const minutes = Math.round(remaining / rate);
  if (minutes < 1) {
    return "under a minute left";
  }
  if (minutes < 60) {
    return `about ${minutes} min left`;
  }
  return `about ${Math.round(minutes / 60)} h left`;
}

interface RunProgressBarProps {
  progress: RunProgress;
  /** The run's start time, for the rate estimate. */
  startedAt: string | null;
}

/** How far a running indexing run has got, with an ETA when one can honestly be given. */
export function RunProgressBar({ progress, startedAt }: RunProgressBarProps): JSX.Element {
  const done = fraction(progress);
  const percent = Math.round(done * 100);
  const remaining = Math.max(0, progress.files_total - progress.files_done);
  const eta = formatEta(remaining, ratePerMinute(progress, startedAt));

  return (
    <div className="run-progress">
      <div
        className="run-progress__track"
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Indexing progress"
      >
        <div className="run-progress__fill" style={{ width: `${percent}%` }} />
      </div>
      <p className="run-progress__detail">
        {progress.containers_total > 0 && (
          <>
            Container {Math.min(progress.containers_done + 1, progress.containers_total)} of{" "}
            {progress.containers_total}
            {progress.container_label && ` — ${progress.container_label}`}
            {" · "}
          </>
        )}
        {progress.files_total > 0
          ? `${progress.files_done.toLocaleString()} / ${progress.files_total.toLocaleString()} files`
          : "listing files…"}
        {eta && ` · ${eta}`}
      </p>
      <p className="run-progress__detail">
        {progress.inserted.toLocaleString()} new · {progress.updated.toLocaleString()} updated ·{" "}
        {progress.deleted.toLocaleString()} removed
        {progress.files_unchanged > 0 &&
          ` · ${progress.files_unchanged.toLocaleString()} already indexed`}
      </p>
    </div>
  );
}
